// ** React Imports
import { Link, useLocation } from "react-router-dom";

// ** Icons
import PlayCircleOutline from "mdi-material-ui/PlayCircleOutline";
import CheckCircle from "mdi-material-ui/CheckCircle";
import ArrowRight from "mdi-material-ui/ArrowRight";

// ** Utils (reused)
import { courseProgress } from "src/@core/utils/course-progress";
import { accessEnded } from "src/@core/utils/course-date";
import { pathWithPreservedSearch } from "src/@core/utils/urlQueryPreservation";
import moment from "moment";
// Chapter availability gate (drip / scheduled / teaser-locked) — reused from the player
import { isChapterAvailable } from "src/layouts/components/coursePlayer/PlayList/helper";

const defaultThumbnail = "/images/default-course.png";

/******************************************************************
 * ##: moduleUnlockLabel
 * Short "Disponível dentro de …" text for a not-yet-open module (drip /
 * scheduled / teaser), shown on the card's CTA. Mirrors the player's
 * ChapterAvaliableIn wording; kept flix-local so the shared chip is untouched.
 * @param {object} chapter - the flix_module availability subset
 * @param {string} courseAddedAt - enrolment date (drip anchor)
 * @param {boolean} hasTeaserAccess - learner is on the free/teaser tier
 * @returns {string} - the button label
 * History:
 * 07-10-2026: Created
 ******************************************************************/
const moduleUnlockLabel = (chapter, courseAddedAt, hasTeaserAccess) => {
  if (hasTeaserAccess === true && chapter?.teaser_locked === true) return "Não disponível na versão gratuita";
  const statusCode = Number(chapter?.status_code);
  let unlockAt;
  if (statusCode === 2 && chapter?.drip_days !== undefined) unlockAt = moment.utc(courseAddedAt).add(chapter.drip_days, "days");
  else if (statusCode === 3 && chapter?.avaliable_on !== undefined) unlockAt = moment.utc(chapter.avaliable_on);
  if (unlockAt && unlockAt.isAfter(moment.utc())) return `Disponível dentro de ${unlockAt.fromNow(true)}`;
  return "Disponível em breve";
};

/******************************************************************
 * ##: FlixCard
 * Course card for the Flix rails. Reuses the app's style-v3 course card
 * markup/CSS (thumbnail, title, lesson chip, "O teu progresso" bar,
 * full-width CTA) wrapped in a `.spv3` scope so it renders like the
 * original and follows the theme. The WHOLE card is a single link into
 * the Flix player (image, title, progress and button all navigate); the
 * CTA is a visual element to avoid nested anchors. Ended courses render
 * a static, non-clickable card. Compact for the rail via `.flix-rail-card`.
 * @param {Object} props - { data } course record; { loading } skeleton
 * @returns {JSX.Element} - the `.spv3 .spv3-course` rail card
 * History:
 * 22-07-2026: Created
 * 22-07-2026: Rebuilt on the style-v3 course card; whole-card link
 ******************************************************************/
const FlixCard = ({ data, loading = false, variant }) => {
  const location = useLocation();

  if (loading || !data) return <FlixCardSkeleton />;

  const progress = courseProgress(data);
  const lessons = data?.courses_students?.lessons_count || data?.active_lessons_count || 0;
  const ended = accessEnded(data?.courses_students?.ended_at);
  const done = progress >= 100;
  // "Continuar a ver" variant: single-line title + resume-lesson name below it,
  // lessons count moved onto the image (top-right), so the card sits lower.
  const isContinue = variant === "continue";
  const resumeLessonTitle = data?.flix_resume_lesson_title;
  // Module cards (flix_split_by_class) carry their own availability. When the
  // module isn't open yet, the CTA shows the limitation ("Disponível dentro
  // de…") and drops the arrow — the card stays clickable (opens the player's
  // availability panel). Non-module cards have no flix_module => available.
  const mod = data?.flix_module;
  const locked = !!mod && !ended && !isChapterAvailable(mod, data?.flix_course_added_at, data?.flix_teaser_access);
  const lockedLabel = locked ? moduleUnlockLabel(mod, data?.flix_course_added_at, data?.flix_teaser_access) : null;
  const to = pathWithPreservedSearch(
    data?.flix_module_id ? `/flix/courses/${data?.course_id}/module/${data.flix_module_id}` : `/flix/courses/${data?.course_id}`,
    location.search,
  );
  const ctaLabel = ended
    ? "Acesso terminado"
    : done
    ? "Rever curso"
    : data?.courses_students?.started === true
    ? "Continuar a ver"
    : "Iniciar curso";

  const inner = (
    <>
      <div className="spv3-course__media">
        {done && (
          <span className="spv3-course__badge spv3-pill spv3-pill--success">
            <CheckCircle />
            Concluído
          </span>
        )}
        {isContinue && lessons > 0 && (
          <span className="flix-course__lessons-tag">
            <PlayCircleOutline />
            {lessons === 1 ? "1 aula" : `${lessons} aulas`}
          </span>
        )}
        <img className="spv3-course__img" alt={data?.title} src={data?.image || defaultThumbnail} draggable={false} />
      </div>

      <div className="spv3-course__body">
        <h3 className="spv3-course__title">{data?.title}</h3>

        {isContinue ? (
          resumeLessonTitle && (
            <p className="flix-course__lesson" title={resumeLessonTitle}>
              {resumeLessonTitle}
            </p>
          )
        ) : (
          <div className="spv3-course__meta">
            <span className="spv3-chip">
              <PlayCircleOutline />
              {lessons === 1 ? "1 aula" : `${lessons} aulas`}
            </span>
          </div>
        )}

        <div className="spv3-course__progress">
          <div className="spv3-course__progress-top">
            <span className="spv3-course__progress-label">{done ? "Curso concluído" : "O teu progresso"}</span>
            <span className="spv3-course__pct">{progress}%</span>
          </div>
          <div className="spv3-course__track">
            <div className="spv3-course__fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="spv3-course__foot">
        <span className={`spv3-btn spv3-btn--block${ended ? " is-disabled" : " spv3-btn--primary"}${locked ? " flix-btn--soon" : ""}`}>
          {locked ? lockedLabel : ctaLabel}
          {!ended && !locked && <ArrowRight className="spv3-btn__arrow" />}
        </span>
      </div>
    </>
  );

  return (
    <div className={`spv3 flix-rail-card${isContinue ? " flix-rail-card--continue" : ""}`}>
      <article className={`spv3-course${done ? " spv3-course--done" : ""}`}>
        {ended ? (
          <div className="flix-card-link flix-card-link--static">{inner}</div>
        ) : (
          <Link className="flix-card-link" to={to} title={data?.title} draggable={false}>
            {inner}
          </Link>
        )}
      </article>
    </div>
  );
};

/******************************************************************
 * ##: FlixCardSkeleton
 * Loading placeholder mirroring the card layout (style-v3 skeleton),
 * so the rail does not jump when the real courses arrive.
 * @returns {JSX.Element} - a shimmering rail card
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const FlixCardSkeleton = () => (
  <div className="spv3 flix-rail-card">
    <article className="spv3-course spv3-course--loading">
      <div className="spv3-course__media">
        <div className="spv3-skeleton spv3-sk-thumb" style={{ height: "100%" }} />
      </div>
      <div className="spv3-course__body">
        <div className="spv3-skeleton spv3-sk-line" style={{ width: "90%" }} />
        <div className="spv3-skeleton spv3-sk-line" style={{ width: "55%" }} />
        <div className="spv3-course__progress">
          <div className="spv3-skeleton spv3-sk-line" style={{ width: "40%", marginBottom: 10 }} />
          <div className="spv3-skeleton" style={{ height: 8, borderRadius: 999 }} />
        </div>
      </div>
      <div className="spv3-course__foot">
        <div className="spv3-skeleton" style={{ height: 36, borderRadius: 11 }} />
      </div>
    </article>
  </div>
);

export default FlixCard;
